import { Venue } from "../store/events.data";
import { ExhibitorStand, ExhibitorWithStand } from "../store/exhibitors.data";
import { Program, ProgramWithVenue, Session, TimeZoneDaylightDetails, TimeZoneDetails } from "../store/programs.data";
import { findTimeZone, getUTCOffset as rawGetUTCOffset } from 'timezone-support'; // Apparently this compiles fine on the back end.  Thank god for small blessings.
import { AUS_SYD_TIMEZONE, DAY_MS, DayAbbr, MonthAbbr } from "./constants";
import { FilterGroup } from "../../globals/store/filters.data";

interface StrangeTimestamp1 { seconds: number, nanoseconds: number, toDate?: Function }
interface StrangeTimestamp2 { _seconds: number, _nanoseconds: number, toDate?: Function }
export type StrangeTimestamp = StrangeTimestamp1 | StrangeTimestamp2


export const createId = (): string => (new Date()).getTime().toString(36) + Math.random().toString(36).slice(2);

export const eventDateStringJoiner = (dates_string: string[]): string => !dates_string ? '' : (dates_string.map((d: any)=>  `${d}`).join('').replace(' ,', ',') as string).trim()

export const justTimeToMS = (justTime: JustTime): number => (justTime.h*60 + justTime.m)*60*1000 
export const HHMMToMs = (s: string): number => justTimeToMS(HHMMToJustTime(s))

export const minutesToHHMM = (mins: number): JustTime => ({ h: Math.floor(Math.abs(mins)/60), m: Math.abs(mins)%60, negative: mins < 0, is0: mins === 0 })
export const HHMMToJustTime = (s: string): JustTime => {
  if (!/^\d{1,2}:\d{2}$/.test(s)) {
    // console.log(`Invalid HHMM! (${s})`)
    return { h: 0, m: 0 }
  }
  const [ h, m ] = s.split(":")
  return { h: +h, m: +m }
}
export const justTimeToHHMM = (t?: JustTime, isClockTime = false): string => !t ? null : `${t.negative ? '-' : ''}${ isClockTime && t.h < 10 ? '0' : ''}${t.h}:${t.m < 10 ? '0' : ''}${t.m}`

export const justTimeToHHMMSS = (justTime?: JustTime) => {
  if (!justTime) return "0:00:00"
  return `${justTimeToHHMM(justTime)}:${justTime.s > 9 ? '' : '0'}${justTime.s}`
}

export const timeConvert = (mh): JustTime => {
  const tt = mh.match(/(\d{1,2}):(\d{1,2})/);
  if (!tt || tt.length < 2) {
    return { h: 0, m: 0 } as JustTime;
  } else {
    return { h: +tt[1], m: +tt[2] } as JustTime;
  }
}

export const listsShareSomeElement = (a: string[], b: string[]) =>
  !!a && !!b && b.some(el => a.includes(el))

export const elementInList = (el: string, list: string[]) =>
  !!list && list.includes(el)

export const getNestedSet = <T, K extends keyof T, I extends ArrayProperties<T,K>>(objectList: T[], arrayKey: K): Set<I> => 
  new Set(flatten(objectList.map(entry => (entry[arrayKey] || []) as unknown as I[]).filter(array => array)))

export const removePropertyFromObjectList = <O>(objectList: O[], prop: keyof O) => objectList.forEach(obj => {
  delete obj[prop]
})
export const addPropertyToObjectList = <O, K extends keyof O>(objectList: O[], prop: K, value: O[K]) => objectList.forEach(obj => {
  obj[prop] = value
})

export const getProgramDayFilters = (filters: FilterGroup) => {
  const programDayFilters: { [pId: string]: Set<number> } = {}
  const anyProgramAtLeastPartiallySelected = !!filters.programs || !!filters.days || !!filters.programLabels
  if(filters.days){
    filters.days.forEach(id => {
      const [ pId, day ] = id.split(":")
      if (!programDayFilters[pId]) programDayFilters[pId] = new Set()
      programDayFilters[pId].add(+day)
    })
  }
  
  return { programDayFilters, anyProgramAtLeastPartiallySelected }
}


export const getUTCstringFromProgramAndTime = (dateString:string, timeZoneId:string, time: string, dayOffset?:number):string => { // expect program to have timeZoneDetails.offsetText
  let unTimeZonedDate = new Date(dateString)
  // console.log({ untimeZonedDate: unTimeZonedDate.getTime() })
  if(dayOffset) {
    unTimeZonedDate = new Date(unTimeZonedDate.getTime() + 1000 * 60 * 60 * 24 * dayOffset) 
    dateString = unTimeZonedDate.getFullYear() + '-' + (unTimeZonedDate.getMonth()+1) + '-' + unTimeZonedDate.getDate();
  }
  
  const TZD:TimeZoneDaylightDetails = getUTCOffset(timeZoneId, unTimeZonedDate)
  if(!TZD || !TZD.offsetText) return '';

  const dateTimeString = dateString + ' ' + time + ':00 GMT' + TZD.offsetText;
  const date = new Date(dateTimeString);
  // console.log({ timeZonedDate: date.getTime() })
  let month = (date.getUTCMonth() + 1).toString();
  month = month.length === 1 ? '0'+month.toString() : month;
  let day = date.getUTCDate().toString();
  day = day.length === 1 ? '0'+day.toString() : day;
  let hour = date.getUTCHours().toString();
  hour = hour.length === 1 ? '0'+hour.toString() : hour;
  let minute = date.getUTCMinutes().toString();
  minute = minute.length === 1 ? '0'+minute.toString() : minute;
  const utc = date.getUTCFullYear() + month + day + 'T' + hour + minute + '00';
  return utc
}

export const addLeadingSpace = (s: string) => s ? ' ' + s : ''

export const getList = <T, K extends keyof T>(objectList: T[], propKey: K): T[K][] => objectList.map(o => o[propKey])

export const convertObjectMapToList = <T>(objectMap: { [id:string]: T }) => Object.values(objectMap)
export const compactDate = (d: Date): string => `${DayAbbr[d.getDay()]} ${d.getDate()} ${capitalizeFirst(MonthAbbr[d.getMonth()])} ${d.getFullYear()}` // Thu 31 Oct 2019.
export const capitalizeFirst = s => s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1, s.length).toLowerCase()

export const listOfIdsToIndexIdMap = (list): { [index: number]: string } =>
  Object.assign({}, ...list.map((a, i) => ({ [i]: a })))
export const listOfIdsToIdIndexMap = (list): { [id: string]: number } =>
  Object.assign({}, ...list.map((a, i) => ({ [a]: i })))


export const date_id = (d: any): string => {
  if (!d) { return ''; }
  if (typeof d === 'string') {
    return d.replace(' ', '');
  } else {
    if (d['toDate']) {
      return formatDate(d.toDate());
    } else if (d.seconds || d._seconds) {
      return formatDate(unknownDateToDate(d))
    } else {
      return formatDate(d);
    }
  } 
}
function formatDate(d) {
  const month = (d.getMonth() + 1)
  const day = d.getDate()
  const year = d.getFullYear()
  return `${year}${month < 10 ? '0' : ''}${month}${day < 10 ? '0' : ''}${day}`
}

export const removeUndefinedsInPlace = <T extends Object>(t: T): T => {
  Object.entries(t).forEach(([k, v]) => {
    if (v === 'undefined') delete t[k]
  })
  return t
}
export const makeProgramText = (title: string, location: string, partialVenue: Partial<Venue>, hideVenueAddress = false, hideVenueName = false) => {
  if (!partialVenue) partialVenue = {}
  let { venue, address } = partialVenue
  if (hideVenueAddress) address = null
  if (hideVenueName) venue = null
  return `${title || ''}${title && (location || venue || address) ? ' • ' : ''}${location || ''}${location && (venue || address) ? ', ' : ''}${venue || ''}${venue && address ? ', ' : ''}${address || ''}`
}
   // this is a helper function because KT decided to change the session structures for OSS Timeline vs Program Timeline



export const simpleHasher = (s: string): number => {
  let hash = 0
  if (!s.length) return hash
  for (let i = 0; i < s.length; i++) {
    const char = s.charCodeAt(i)
    /* tslint:disable */
    hash = ((hash << 5) - hash) + char
    hash = hash & hash
    /* tslint:enable */
  }
  return hash
}

export const updateProgramsToNewVenueStructureAndExtractOldVenues = (venueIds: Set<string>, programs: Program[], defaultTimeZoneId: string): Venue[] => {
  // mutates programs in place, returns new venues (easiest way to make this work nice with program service)
  const newVenues: Venue[] = []
  programs.forEach(p => {
    const { venueId, venue, address, latitude, longitude, timeZoneDetails, date } = p as ProgramWithVenue
    if (venueId || !venue || !address) return
    const venueHash = `${simpleHasher(`${venue || ''}${address || ''}`) || ''}` // simpleHasher returns 0 for empty string which gets turned into an empty string here
    p.venueId = venueHash
    if (venueIds.has(venueHash)) return
    venueIds.add(venueHash)
    newVenues.push({
      id: venueHash,
      venue: venue || null,
      address: address || null,
      latitude: latitude || null,
      longitude: longitude || null,
      timeZoneId: timeZoneDetails ? timeZoneDetails.timeZoneId : defaultTimeZoneId
    })
  })
  return newVenues
}

export const updateProgramsAndVenuesInPlace = (venues: { [vId: string]: Venue } = {}, programs: Program[] = [], defaultTimeZoneId: string = AUS_SYD_TIMEZONE) => { // updates in place for share models that don't need the actual id list
  // console.log({ venues, programs, defaultTimeZoneId })
  if (!programs) return
  if (!defaultTimeZoneId) defaultTimeZoneId = AUS_SYD_TIMEZONE
  const newTimezones = updateProgramsToNewVenueStructureAndExtractOldVenues(new Set(Object.keys(venues)), programs, defaultTimeZoneId)
  newTimezones.forEach(tz => venues[tz.id] = tz)
}

export const makeDefaultLabels = () => [
  { id: 'default01', label: '', color: '#54DDC7', name: 'Green' }, 
  { id: 'default02', label: '', color: '#FFD157', name: 'Yellow' },
  { id: 'default03', label: '', color: '#FF8A5C', name: 'Orange' }, 
  { id: 'default04', label: '', color: '#F05454', name: 'Red' },
  { id: 'default05', label: '', color: '#8270DB', name: 'Purple' }, 
  { id: 'default06', label: '', color: '#A6DBFF', name: 'Blue' }
]

export type AllSameKeyValuesEnforcer<T> = { [P in keyof Required<T>]: P }
export type AllSameKeysEnforcer<T> = { [P in keyof Required<T>]: any }
export type AllSameKeysToTypeEnforcer<T, V extends unknown> = { [P in keyof Required<T>]: V }
export type AllSameKeyValueTypesEnforcer<T> = { [P in keyof Required<T>]: T[P] }

export type AllSameKeySetEnforcer<T> = { [P in keyof Required<T>] }
export type AllSameKeysEnforcerWithValue<T, V> = { [P in keyof Required<T>]: V }
export type AllSameKeysAsPropertyValueEnforcer<T> = { value: keyof T, selected: boolean }[]
export const enumKeyValuesMatch = <T>(kv: { [K in keyof T]: K }) => {}

export interface JustTime {
  h: number;
  m: number;
  s?: number;
  negative?: boolean
  is0?: boolean
}

export const sortByProp = <T, K extends keyof T>(prop: K, direction: number = 1) => (a: T, b: T) => !a[prop] ? -direction : !b[prop] ? direction : (a[prop] + '').toLocaleUpperCase() < (b[prop] + '').toLocaleUpperCase() ? -direction : direction
export const sortByPropOfNumber = <T, K extends keyof T>(prop: K, direction: number = 1) => (a:T, b:T) => !a[prop] ? -direction : !b[prop] ? direction : a[prop] < b[prop] ? -direction : direction

export const makeMapFromListOfIds = (ids: string[]): { [key: string]: string } => Object.assign({}, ...ids.map(id => ({ [id]: id })))

export const makeMapFromListOfObjectsAndKey = <O>(objectList: O[], key: keyof O): { [key: string]: O } => Object.assign({}, ...objectList.filter(o => o[key]).map(o => ({ [o[key as string]]: o })))

export const makeMapFromListOfObjectsAndKeyToProperty = <O, K extends keyof O, P extends keyof O, PT extends O[P]>(objectList: O[], key: K, prop: P): { key: PT } => Object.assign({}, ...objectList.filter(o => o[key]).map(o => ({ [o[key as string]]: o[prop] })))

export function createSameKeyValues<T extends string>(values: readonly T[]): { [K in T]: K } {
  return values.reduce((acc, value) => {
    acc[value] = value;
    return acc;
  }, {} as { [K in T]: K });
}

export const jsonCopyObject = <T extends unknown>(obj: T) => obj ? <T>JSON.parse(JSON.stringify(obj)) : obj

export const typedValues = <T>(dictionary: { [id:string]: T }): T[] => Object.values(dictionary)

export const flatten = <T>(nestedArray: T[][], filter = true, depth = 1): T[] => {
  if (depth !== 1) throw new Error("Function flatten not implemented for depths greater than 1 yet!")
  const array: T[] = []
  nestedArray.filter(arr => !!arr?.length).forEach(arr => (!filter ? arr : arr.filter(o => !!o)).forEach(o => array.push(o)))
  return array
}


export const getSet = <T extends any, K extends keyof T>(objectList: T[], propKey: K): Set<T[K]> =>
  !objectList ? new Set() : new Set(objectList.map(entry => entry[propKey]).filter(prop => prop))



export const setIntersection = <T extends unknown>(setA: Set<T>, setB: Set<T>): Set<T> => {
  const smallSet = setA.size < setB.size ? setA : setB
  const bigSet = setA === smallSet ? setB : setA
  const intersectionSet: Set<T> = new Set()
  smallSet.forEach(e => {
    if (bigSet.has(e)) {
      intersectionSet.add(e)
    }
  })
  return intersectionSet
}


export const getNestedList = <T, K extends keyof T, I extends ArrayProperties<T, K>>(objectList: T[], arrayKey: K): I[] => flatten(objectList.map(o => (o[arrayKey] || []) as unknown as I[]))

export type ArrayProperties<T, K extends keyof T> = T[K] extends Array<any> ? T[K][0] : any

export const createSimpleId = (): string => Math.random().toString(36).slice(2) // when collisions are not an issue at all

export const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
export const isEmailValid = email => emailRegex.test(email.toLowerCase())

export const unknownDateToDate = (d: StrangeTimestamp1 | StrangeTimestamp2 | string | Date): Date => {
  if (!d) return null
  if (typeof d === 'string') return new Date(d)
  if (d['toDate']) return d['toDate']()
  const { seconds, _seconds, nanoseconds, _nanoseconds } = d as any
  if (seconds) return new Date(seconds*1000 + nanoseconds / 1000)
  if (_seconds) return new Date(_seconds*1000 + _nanoseconds / 1000)
  return new Date(d as any)
}
export const stripBackslashes = (id: string) => Array.from(id).filter(c => c !== '/').join("")

export const str2Date = (id: string): Date => {
  if (!id) return new Date()
  try {
    id = stripBackslashes(id)
    const y = id.substr(0, 4);
    const m = id.substr(4, 2);
    const d = id.substr(6, 2);
    const date = new Date(+y, +m - 1, +d)
    // console.log({ date, y, m, d, id })
    return date
  } catch (e) {
    return new Date()
  }
}


export const time_to_min = (mh: string): number => {
  const tt = mh.match(/(\d{1,2}):(\d{1,2})/);
  if (!tt || tt.length < 2) {
    return 0;
  } else {
    return +tt[1] * 60 + +tt[2];
  }
}

export const getDefaultTimeZoneDetails = (numberOfDays:number = 1): TimeZoneDetails => {
   const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone
   const timeZoneDaylightDetails = getUTCOffset(timeZoneId, new Date())
   const days = new Array(numberOfDays).fill({...timeZoneDaylightDetails})
   return {
     timeZoneId,
     ...timeZoneDaylightDetails,
     days
   }
}   

export const date2str = (d: Date): string => {
  if (!d) {
    return '';
  } else {
    try {
      const mm = d.getMonth() + 1;
      const dd = d.getDate();  // GH - I think it makes sense to have the date double digit too.
      return d.getFullYear() + '/' + (mm < 10 ? '0' : '') + mm + '/' + (dd < 10 ? '0' : '') + dd;
    } catch (e) {
      console.error('date2str error: ', e)
    }
  }
  return ''
}

export const addDaysToDate = (date:Date, daysToAdd: number) => new Date(date.getTime() + daysToAdd*DAY_MS)

export const getDateStringFromProgramAndSession = (p: Program, s: Session) => {
  if (!p || !p.date || !s || !s.on_day) return null
  return s.on_day === 1 ? p.date : date2str(addDaysToDate(str2Date(p.date), s.on_day - 1))
}

export const dateIdCompare = (a: string, b: string, smaller: boolean = true) => {
  const aDate = new Date(a)
  const bDate = new Date(b)
  return smaller ? (aDate < bDate ? a : b) : (aDate < bDate ? b : a)
}

export const pruneObject = <O extends Object>(o: O, black_set: Set<keyof O>): O => {
  black_set.forEach(k => delete o[k]) // mutates in place, care
  return o
}

const setListWrapper = <T extends unknown>(listA: T[], listB: T[], setFunction): T[] => Array.from(setFunction(new Set(listA), new Set(listB)))
export const listUnion = <T extends unknown>(listA: T[], listB: T[], ): T[] => setListWrapper(listA, listB, setUnion)
export const listDifference = <T extends unknown>(initialList: T[], listToRemove: T[], ): T[] => setListWrapper(initialList, listToRemove, setDifference)
export const listIntersection = <T extends unknown>(listA: T[], listB: T[]): T[] => setListWrapper(listA, listB, setIntersection)


export const setUnion = <T extends unknown>(setA: Set<T>, setB: Set<T>): Set<T> => {
  const smallSet = setA.size < setB.size ? setA : setB
  const bigSet: Set<T> = new Set(setA === smallSet ? setB : setA)
  smallSet.forEach(el => bigSet.add(el))
  return bigSet
}

export const setDifference = <T extends unknown>(initialSet: Set<T>, setToRemove: Set<T>): Set<T> => {
  const differenceSet: Set<T> = new Set(initialSet)
  setToRemove.forEach(e => differenceSet.delete(e))
  return differenceSet
}

export const copyObjectWithProps = <O extends unknown>(propsToCopy: Set<string>, obj: O): O => {
  if (!obj) return null
  const objCopy = <O>{}
  Object.entries(obj).forEach(([key, value]) => {
    if (propsToCopy.has(key) && value !== undefined)
      objCopy[key] = value
  })
  return objCopy
}

export const exhibitorToStand = ({ programId, standNumber, standNotes, standTypeId, id }: ExhibitorWithStand): ExhibitorStand => 
  !standNotes && !standTypeId && !standNumber && !programId ? null : {
    id, 
    programId: programId,
    exhibitorId: id, 
    name: standNumber || '',
    standNotes: standNotes || '', 
    standTypeId: standTypeId || null,
    assetIds: [],
    requests: []
  }

export const getLastTruthyInList = <T>(list: T[]): T => list.filter(el => !!el).slice(-1)[0] // returns undefined if empty

export type ExcludedKeys<A,B> = (keyof Required<Omit<A, keyof B>>)[]

export const getUTCOffset = (timeZoneId: string, date: Date): TimeZoneDaylightDetails => {
  const timezone = findTimeZone(timeZoneId);
  const theUtcobj = rawGetUTCOffset(new Date(date), timezone);
  const offset = theUtcobj.offset / 60 * -1;
  const hours = Math.abs(Math.floor(offset));
  const minutes = Math.abs(offset * 60 % 60);
  const offsetText = (offset < 0 ? '-' : '+')+(hours > 9 ? hours : '0'+hours) + ':' + (minutes > 9 ? minutes : '0'+minutes)
  return {offset: offset * -1, abbr: theUtcobj.abbreviation, offsetText};
  // return { offset: 0, abbr: 'UTC', offsetText: '+00:00' }
}